import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import { getFirebaseAuth, getGoogleProvider } from '@/lib/firebase';
import {
  firebaseAuthApi,
  updateProfileApi,
  hasSession,
  setSession,
  clearSession,
} from '@/lib/auth';
import { mapAuthError, isUserCancelled } from '@/lib/authErrors';
import type { User } from '@/types/auth';

interface AuthState {
  // State
  user: User | null;
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    firstName: string,
    lastName: string,
    email: string,
    password: string
  ) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  logout: () => Promise<void>;
  initAuth: () => () => void;
  updateProfile: (
    data: Partial<
      Pick<User, 'first_name' | 'last_name' | 'phone' | 'is_first_time_buyer' | 'email_notifications'>
    >
  ) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  firebaseUser: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  /**
   * Sign in with Google via Firebase popup (loads the Firebase SDK on demand)
   */
  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });

    try {
      const [auth, provider, { signInWithPopup }] = await Promise.all([
        getFirebaseAuth(),
        getGoogleProvider(),
        import('firebase/auth'),
      ]);
      const result = await signInWithPopup(auth, provider);
      const firebaseToken = await result.user.getIdToken();

      // Send the Firebase token to the backend to create/get the user session
      const response = await firebaseAuthApi(firebaseToken);

      setSession();
      set({
        user: response.user,
        firebaseUser: result.user,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (err) {
      // Don't show error for user-cancelled popups
      if (isUserCancelled(err)) {
        set({ isLoading: false, error: null });
        return;
      }

      set({ isLoading: false, error: mapAuthError(err) });
      throw err;
    }
  },

  /**
   * Sign in with email + password
   */
  signInWithEmail: async (email, password) => {
    set({ isLoading: true, error: null });

    try {
      const [auth, { signInWithEmailAndPassword }] = await Promise.all([
        getFirebaseAuth(),
        import('firebase/auth'),
      ]);
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const firebaseToken = await cred.user.getIdToken();
      const response = await firebaseAuthApi(firebaseToken);

      setSession();
      set({
        user: response.user,
        firebaseUser: cred.user,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (err) {
      set({ isLoading: false, error: mapAuthError(err) });
      throw err;
    }
  },

  /**
   * Create an account with email + password.
   * displayName is set before the token exchange (with a forced refresh) so
   * the backend's auto-registration receives the name claim.
   */
  signUpWithEmail: async (firstName, lastName, email, password) => {
    set({ isLoading: true, error: null });

    try {
      const [auth, { createUserWithEmailAndPassword, updateProfile }] = await Promise.all([
        getFirebaseAuth(),
        import('firebase/auth'),
      ]);
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const displayName = `${firstName} ${lastName}`.trim();
      if (displayName) {
        await updateProfile(cred.user, { displayName });
      }
      // Fire-and-forget: verification is encouraged, never blocks signup
      import('firebase/auth')
        .then(({ sendEmailVerification }) => sendEmailVerification(cred.user))
        .catch(() => {});
      const firebaseToken = await cred.user.getIdToken(true);
      const response = await firebaseAuthApi(firebaseToken);

      // Belt & braces: if the name claim didn't reach the backend, set it now.
      let user = response.user;
      if (firstName && !user.first_name) {
        try {
          user = await updateProfileApi(firebaseToken, {
            first_name: firstName,
            last_name: lastName,
          });
        } catch {
          // profile page still allows fixing the name later
        }
      }

      setSession();
      set({
        user,
        firebaseUser: cred.user,
        isLoading: false,
        isInitialized: true,
        error: null,
      });
    } catch (err) {
      set({ isLoading: false, error: mapAuthError(err) });
      throw err;
    }
  },

  /**
   * Send a password-reset email
   */
  resetPassword: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const [auth, { sendPasswordResetEmail }] = await Promise.all([
        getFirebaseAuth(),
        import('firebase/auth'),
      ]);
      await sendPasswordResetEmail(auth, email);
      set({ isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: mapAuthError(err) });
      throw err;
    }
  },

  /**
   * Re-send the address-verification email for the signed-in user
   */
  sendVerificationEmail: async () => {
    const { firebaseUser } = get();
    if (!firebaseUser) {
      throw new Error('Not signed in');
    }
    const { sendEmailVerification } = await import('firebase/auth');
    await sendEmailVerification(firebaseUser);
  },

  /**
   * Sign out from both Firebase and clear local state
   */
  logout: async () => {
    clearSession();
    try {
      const [auth, { signOut }] = await Promise.all([
        getFirebaseAuth(),
        import('firebase/auth'),
      ]);
      await signOut(auth);
    } catch {
      // Continue with local cleanup even if Firebase sign-out fails
    }
    set({
      user: null,
      firebaseUser: null,
      error: null,
    });
  },

  /**
   * Initialize auth state. Skips loading Firebase entirely unless a previous
   * session flag exists — anonymous visitors never download the SDK.
   * Returns an unsubscribe function.
   */
  initAuth: () => {
    if (get().isInitialized || typeof window === 'undefined') {
      return () => {};
    }

    if (!hasSession()) {
      set({ isInitialized: true });
      return () => {};
    }

    set({ isLoading: true });

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    (async () => {
      const [auth, { onAuthStateChanged, signOut }] = await Promise.all([
        getFirebaseAuth(),
        import('firebase/auth'),
      ]);
      if (cancelled) return;

      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          try {
            const token = await firebaseUser.getIdToken();
            const response = await firebaseAuthApi(token);

            setSession();
            set({
              user: response.user,
              firebaseUser,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          } catch {
            // Firebase user exists but backend rejected it - sign out
            clearSession();
            await signOut(auth);
            set({
              user: null,
              firebaseUser: null,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          }
        } else {
          clearSession();
          set({
            user: null,
            firebaseUser: null,
            isLoading: false,
            isInitialized: true,
            error: null,
          });
        }
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },

  /**
   * Update the current user's profile
   */
  updateProfile: async (data) => {
    const { firebaseUser } = get();
    if (!firebaseUser) {
      throw new Error('Authentication required');
    }

    set({ isLoading: true, error: null });

    try {
      const token = await firebaseUser.getIdToken();
      const user = await updateProfileApi(token, data);
      set({ user, isLoading: false, error: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  /**
   * Clear any error message
   */
  clearError: () => {
    set({ error: null });
  },
}));
